import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from '@/components/UI/Accordion';
import { DateRenderer, DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import UserAvatar from '@/components/UI/UserAvatar';
import { formatNumber, hasOwnProperty } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { Measurement } from '@/model/Measurement.model';
import { Preset } from '@/model/Preset.model.ts';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { StudyApiId } from '@/model/Study.model';
import { sortDescByDateStrProperty } from '@/utils/array.ts';
import type { FC } from 'react';
import Actions from './Actions';

interface MeasurementListProps {
  preset: Preset;
  studyApiId: StudyApiId;
  subject: Animal;
  canEdit: boolean;
  measurements: Array<Measurement>;
  activeCalculation: PresetCalculation;
  handleCallback: (message: string, success?: boolean) => Promise<void>;
  readOnlyMeasurementIds: string[];
}

const MeasurementList: FC<MeasurementListProps> = ({
  preset,
  canEdit = false,
  studyApiId,
  subject,
  measurements,
  activeCalculation,
  handleCallback,
  readOnlyMeasurementIds,
}) => {
  const filteredMeasurements = measurements.filter((m) =>
    hasOwnProperty(m.variables ?? {}, activeCalculation.id ?? null)
  );
  const orderedMeasurements = filteredMeasurements.sort(sortDescByDateStrProperty('measured_at'));

  return (
    <Accordion>
      {orderedMeasurements.map((m) => {
        const relevantVariable = m.variable_list?.find((variable) => variable.name === activeCalculation.id);
        const userCreated = relevantVariable?.created_by;
        const userLastAccessed = relevantVariable?.updated_by;

        const isCurrentMeasurementReadOnly = readOnlyMeasurementIds.includes(activeCalculation.id);
        const showActions = canEdit && !isCurrentMeasurementReadOnly;

        return (
          <AccordionItem key={m.id}>
            <AccordionHeader className="f6 flex flex-wrap justify-between items-center mh3">
              <span
                className="near-black"
                data-test-key={`measurement-list-summary-${m.measured_at}-${activeCalculation.id}`}
              >
                {formatNumber(m.variables[activeCalculation.id])} {activeCalculation.unit}
              </span>
              <DateRenderer value={m.measured_at} />
            </AccordionHeader>
            <AccordionPanel>
              <div className="ph3 pb3 flex">
                {userCreated && <UserAvatar user={userCreated} tooltip={userCreated.name ?? userCreated.email} />}
                <div className="w-100 pl3">
                  {_notNil(userLastAccessed) && (
                    <p className="f7 lh-copy db pb3">
                      Updated <DateTimeRenderer value={userLastAccessed.datetime} /> by{' '}
                      {userLastAccessed.name || userLastAccessed.email}
                    </p>
                  )}
                  {_isNotEmpty(activeCalculation.measurements) && (
                    <ul className="pa3 br3 br--top--left bg-near-white ma0">
                      {activeCalculation.measurements?.map((v) => (
                        <li key={v.id} className="f6 lh-copy">
                          <span className="near-black">{v.name}:</span>{' '}
                          {m.variables?.[v.id] ? (
                            <>
                              {formatNumber(m.variables[v.id])} {v.unit}
                            </>
                          ) : (
                            ' -'
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {showActions && (
                    <div className="mt2">
                      <Actions
                        studyApiId={studyApiId}
                        subject={subject}
                        preset={preset}
                        measurement={m}
                        activeCalculation={activeCalculation}
                        handleCallback={handleCallback}
                      />
                    </div>
                  )}
                </div>
              </div>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default MeasurementList;
